import React, { useState, useMemo } from "react";
import { portalLink } from "../utils/portal";
import {
  Search,
  MapPin,
  Briefcase,
  Clock,
  ArrowRight,
  X,
  Bookmark,
  BookmarkCheck,
  SlidersHorizontal,
} from "lucide-react";

const C = {
  cream: "#F4F1EA",
  ink: "#141414",
  orange: "#c84b20",
  dark: "#141414",
  border: "rgba(20,20,20,0.16)",
};

const STATS = [
  { value: "120+", label: "Live Openings" },
  { value: "85+", label: "Hiring Studios" },
  { value: "92%", label: "Response Rate" },
  { value: "4", label: "Design Disciplines" },
];

const CATEGORIES = [
  {
    tag: "01 / ARCHITECTURE",
    title: "Master industry-grade roles firms actually hire for.",
    desc: "Studio, site and BIM roles across India's top architecture practices.",
    cta: "BROWSE ROLES",
    bg: "linear-gradient(135deg,#2b2b2b,#0f0f0f)",
  },
  {
    tag: "02 / INTERIOR DESIGN",
    title: "Your next interior role is one application away.",
    desc: "Residential, hospitality and commercial studios hiring now.",
    cta: "BROWSE ROLES",
    bg: "linear-gradient(135deg,#33251c,#141414)",
  },
  {
    tag: "03 / URBAN PLANNING",
    title: "Shape cities with planning teams that ship real projects.",
    desc: "Consultancies and civic bodies building India's urban future.",
    cta: "BROWSE ROLES",
    bg: "linear-gradient(135deg,#1c2620,#141414)",
  },
  {
    tag: "04 / LANDSCAPE",
    title: "Design outdoor spaces for studios that value craft.",
    desc: "Landscape and site-design roles, from concept to construction.",
    cta: "BROWSE ROLES",
    bg: "linear-gradient(135deg,#242424,#111111)",
  },
];

const WHY_ITEMS = [
  { n: "01", title: "Verified Jobs", desc: "Every listing is checked before it goes live — no dead links, no ghost roles." },
  { n: "02", title: "Quick Apply", desc: "Apply to studios in one click with your saved IPDI profile.", highlight: true },
  { n: "03", title: "Built-in Resume Builder", desc: "Build a portfolio-ready resume formatted for design and architecture recruiters." },
  { n: "04", title: "Relevant Matches", desc: "Roles filtered to your discipline, experience level, and city." },
];

const JOBS = [
  { id: 1, title: "Junior Architect", company: "Studio Meridian", location: "Pune, MH", type: "Full-time", category: "Architecture", experience: "Entry-level", posted: "2 days ago", postedRank: 1 },
  { id: 2, title: "Interior Design Intern", company: "Casa Living Interiors", location: "Mumbai, MH", type: "Internship", category: "Interior Design", experience: "Entry-level", posted: "3 days ago", postedRank: 2 },
  { id: 3, title: "Urban Planning Associate", company: "Vantage Urban Labs", location: "Bengaluru, KA", type: "Full-time", category: "Urban Planning", experience: "Mid-level", posted: "5 days ago", postedRank: 3 },
  { id: 4, title: "Landscape Designer", company: "GreenLine Studio", location: "Ahmedabad, GJ", type: "Contract", category: "Landscape", experience: "Mid-level", posted: "1 week ago", postedRank: 4 },
  { id: 5, title: "Senior Architect", company: "Form & Frame Architects", location: "Delhi NCR", type: "Full-time", category: "Architecture", experience: "Senior", posted: "1 week ago", postedRank: 5 },
  { id: 6, title: "Design Visualizer (3D)", company: "Renderworks India", location: "Remote", type: "Full-time", category: "Architecture", experience: "Mid-level", posted: "2 weeks ago", postedRank: 6 },
  { id: 7, title: "Interior Design Intern", company: "Nest & Nook", location: "Chennai, TN", type: "Internship", category: "Interior Design", experience: "Entry-level", posted: "2 weeks ago", postedRank: 7 },
  { id: 8, title: "Urban Planner", company: "Civic Grid Consultants", location: "Hyderabad, TS", type: "Full-time", category: "Urban Planning", experience: "Senior", posted: "3 weeks ago", postedRank: 8 },
];

const CAT_FILTERS = ["Architecture", "Interior Design", "Urban Planning", "Landscape"];
const TYPE_FILTERS = ["Full-time", "Internship", "Contract"];
const EXP_FILTERS = ["Entry-level", "Mid-level", "Senior"];
const SORTS = ["Newest", "A → Z", "Full-time first"];

const PROCESS = [
  { n: "01", title: "Apply Online", desc: "Submit your profile and portfolio in under two minutes." },
  { n: "02", title: "Portfolio Review", desc: "Our team screens fit before your profile reaches the studio." },
  { n: "03", title: "Interview & Prep", desc: "Get mock-interview support before you meet the hiring team." },
  { n: "04", title: "Get Placed", desc: "IPDI coordinates scheduling until you land the right role." },
];

function Eyebrow({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
      <span style={{ width: 22, height: 1.5, background: C.orange }} />
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5, letterSpacing: "0.14em", fontWeight: 500, color: C.orange }}>
        {children}
      </span>
    </div>
  );
}

export default function JobsPage() {
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [sort, setSort] = useState("Newest");
  const [visible, setVisible] = useState(6);
  const [saved, setSaved] = useState([]);
  const [activeFilterTab, setActiveFilterTab] = useState("discipline");

  const toggle = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val]);

  const toggleSave = (id) =>
    setSaved((s) => (s.includes(id) ? s.filter((v) => v !== id) : [...s, id]));

  const filtered = useMemo(() => {
    let result = JOBS.filter((job) => {
      const q =
        query.trim() === "" ||
        job.title.toLowerCase().includes(query.toLowerCase()) ||
        job.company.toLowerCase().includes(query.toLowerCase()) ||
        job.location.toLowerCase().includes(query.toLowerCase());
      const c = categories.length === 0 || categories.includes(job.category);
      const t = types.length === 0 || types.includes(job.type);
      const e = experiences.length === 0 || experiences.includes(job.experience);
      return q && c && t && e;
    });
    if (sort === "A → Z") result = [...result].sort((a, b) => a.title.localeCompare(b.title));
    else if (sort === "Full-time first")
      result = [...result].sort((a, b) => (a.type === "Full-time" ? -1 : 1) - (b.type === "Full-time" ? -1 : 1));
    else result = [...result].sort((a, b) => a.postedRank - b.postedRank);
    return result;
  }, [query, categories, types, experiences, sort]);

  const visibleJobs = filtered.slice(0, visible);
  const activeFilterCount = categories.length + types.length + experiences.length;

  const clearAll = () => {
    setCategories([]);
    setTypes([]);
    setExperiences([]);
    setQuery("");
  };

  const handleStudentRegister = () => {
    window.location.href = '/student-register';
  };

  const handleStudentLogin = () => {
    window.location.href = '/student-login';
  };

  const handleBrowseOpenings = () => {
    document.getElementById('ipdi-listings')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={{ background: C.cream, color: C.ink, fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,700;1,500;1,600&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        .ipdi2-serif { font-family: 'Playfair Display', serif; }
        .ipdi2-mono { font-family: 'JetBrains Mono', monospace; }
        .ipdi2-btn-fill {
        justify-content: space-around; 
          background: ${C.orange}; color: #fff; border: 1.5px solid ${C.orange};
          font-family: 'JetBrains Mono', monospace; font-size: 13px; letter-spacing: 0.06em;
          font-weight: 500; padding: 10px 28px; cursor: pointer; display: inline-flex;
          align-items: center; gap: 10px; transition: opacity .15s ease, transform .1s ease;
        }
        .ipdi2-btn-fill:hover { opacity: 0.88; transform: translateY(-1px); }
        .ipdi2-btn-outline {
          background: transparent; color: ${C.ink}; border: 1.5px solid ${C.ink};
          font-family: 'JetBrains Mono', monospace; font-size: 13px; letter-spacing: 0.06em;
          font-weight: 500; padding: 8px 10px; cursor: pointer; display: inline-flex;
          align-items: center; gap: 10px; transition: background .15s ease, color .15s ease;
        }
        .ipdi2-hero-actions .ipdi2-btn-outline,
        .ipdi2-hero-actions .ipdi2-btn-fill {
          padding: 10px 28px;
          border-width: 1.5px;
          font-size: 13px;
          letter-spacing: 0.06em;
          gap: 10px;
        }
        .ipdi2-btn-outline:hover { background: ${C.ink}; color: #fff; }
        .ipdi2-btn-outline-light {
          background: transparent; color: #fff; border: 1.5px solid rgba(255,255,255,0.35);
          font-family: 'JetBrains Mono', monospace; font-size: 12px; letter-spacing: 0.06em;
          font-weight: 500; padding: 11px 18px; cursor: pointer; display: inline-flex;
          align-items: center; gap: 8px; transition: border-color .15s ease, color .15s ease;
        }
        .ipdi2-btn-outline-light:hover { border-color: ${C.orange}; color: ${C.orange}; }
        .ipdi2-chip {
          font-family: 'JetBrains Mono', monospace; font-size: 12.5px; letter-spacing: 0.04em;
          padding: 9px 16px; border: 1.5px solid ${C.ink}; background: transparent; color: ${C.ink};
          cursor: pointer; transition: background .15s ease, color .15s ease;
        }
        .ipdi2-chip.active { background: ${C.ink}; color: #fff; }
        .ipdi2-chip.orange { border-color: ${C.orange}; color: ${C.orange}; }
        .ipdi2-chip.orange.active { background: ${C.orange}; color: #fff; }
        .ipdi2-card { transition: transform .2s ease, box-shadow .2s ease; }
        .ipdi2-card:hover { transform: translateY(-2px); box-shadow: 0 16px 32px rgba(0,0,0,0.06); }
        .ipdi2-input:focus, .ipdi2-btn-fill:focus-visible, .ipdi2-btn-outline:focus-visible {
          outline: 2px solid ${C.orange}; outline-offset: 2px;
        }
        .ipdi2-checkbox { accent-color: ${C.orange}; width: 16px; height: 16px; cursor: pointer; }
        .ipdi2-save {
          background: none; border: 1.5px solid ${C.border}; padding: 10px; cursor: pointer;
          display: flex; transition: border-color .15s ease;
        }
        .ipdi2-save:hover { border-color: ${C.orange}; }
        select.ipdi2-select { appearance: none; }

        /* WHY APPLY cards — hover animation (background only, no lift) */
        .ipdi2-why-item {
          position: relative;
          cursor: default;
          overflow: hidden;
          transition: background .35s ease, color .35s ease;
        }
        .ipdi2-why-item::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 4px;
          background: ${C.orange};
          transform: scaleX(0);
          transform-origin: left;
          transition: transform .35s cubic-bezier(.22,1,.36,1);
        }
        .ipdi2-why-item:hover::before { transform: scaleX(1); }
        .ipdi2-why-item:hover {
          background: ${C.ink};
          color: #fff;
          z-index: 2;
        }
        .ipdi2-why-num {
          display: inline-block;
          transition: color .3s ease;
        }
        .ipdi2-why-title { transition: color .3s ease; }
        .ipdi2-why-desc { transition: color .3s ease; }
        // .ipdi2-why-item:hover .ipdi2-why-desc { color: #c9c9c9 !important; }

        /* CATEGORY BENTO cards — hover animation */
        .ipdi2-cat-card {
          position: relative;
          cursor: pointer;
          overflow: hidden;
          transition: transform .4s cubic-bezier(.22,1,.36,1), box-shadow .4s ease;
        }
        .ipdi2-cat-card:hover {
          transform: translateY(-10px) scale(1.025);
          box-shadow: 0 28px 56px rgba(0,0,0,0.5);
          z-index: 3;
        }
        .ipdi2-cat-card-inner {
          position: relative;
          z-index: 1;
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          transition: transform .4s cubic-bezier(.22,1,.36,1);
        }
        .ipdi2-cat-card:hover .ipdi2-cat-card-inner {
          transform: translateY(-6px);
        }
        .ipdi2-cat-tag {
          transition: background .3s ease, color .3s ease, transform .3s cubic-bezier(.22,1,.36,1);
        }
        .ipdi2-cat-card:hover .ipdi2-cat-tag {
          // background: ${C.orange};
          color: #fff;
          transform: translateX(4px);
        }
        .ipdi2-cat-card .ipdi2-btn-outline-light {
          transition: background .3s ease, border-color .3s ease, color .3s ease;
        }
        // .ipdi2-cat-card:hover .ipdi2-btn-outline-light {
        //   background: ${C.orange};
        //   border-color: ${C.orange};
        //   color: #fff;
        // }
        .ipdi2-cat-card .ipdi2-btn-outline-light svg {
          transition: transform .3s cubic-bezier(.22,1,.36,1);
        }
        .ipdi2-cat-card:hover .ipdi2-btn-outline-light svg {
          transform: translateX(6px);
        }
        .ipdi2-cat-card::before {
          content: "";
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 30% 20%, rgba(217,98,44,0.35), transparent 60%);
          opacity: 0;
          transition: opacity .4s ease;
          pointer-events: none;
          z-index: 0;
        }
        .ipdi2-cat-card:hover::before { opacity: 1; }
        .ipdi2-cat-card::after {
          content: "";
          position: absolute;
          inset: 0;
          border: 1px solid transparent;
          transition: border-color .35s ease;
          pointer-events: none;
          z-index: 2;
        }
        .ipdi2-cat-card:hover::after { border-color: rgba(217,98,44,0.6); }

        /* Stats row dividers: 4-col on desktop, 2-col on mobile */
        .ipdi2-stat-item {
          border-left: 1px solid ${C.border};
          padding-left: 24px;
        }
        .ipdi2-stat-item.ipdi2-stat-0 { border-left: none; padding-left: 0; }
        .ipdi2-stats-divider { display: none; }

        /* Filter groups: stacked on desktop, tab-switched on mobile */
        .ipdi2-filter-tabs { display: none; }
        .ipdi2-filter-group { display: block; }

        @media (min-width: 861px) {
          .ipdi2-container {
            max-width: 100% !important;
            padding-left: 2.5rem !important;
            padding-right: 2.5rem !important;
          }
        }

        @media (min-width: 1600px) {
          .ipdi2-container {
            max-width: 1400px !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
        }

        /* ---------- Responsive overrides ---------- */

        /* Tablet and below */
        @media (max-width: 860px) {
          .ipdi2-container { padding-left: 24px !important; padding-right: 24px !important; }
          .ipdi2-featured-grid { grid-template-columns: 1fr !important; }
          .ipdi2-featured-image { min-height: 220px; }
          .ipdi2-featured-panel { padding: 32px 26px !important; }
          .ipdi2-how-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .ipdi2-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .ipdi2-stat-item { border-left: none !important; padding-left: 0 !important; }
          .ipdi2-stat-item.ipdi2-stat-2,
          .ipdi2-stat-item.ipdi2-stat-3 { border-top: none !important; padding-top: 0 !important; }
          .ipdi2-stats-divider {
            display: block;
            grid-column: 1 / -1;
            border-top: 1px solid ${C.border};
            margin: 6px 0;
          }
          .ipdi2-layout { grid-template-columns: 1fr !important; }
          .ipdi2-sidebar { position: static !important; }

          .ipdi2-filter-tabs {
            display: flex !important;
            gap: 6px;
            margin-bottom: 20px;
            border: 1.5px solid ${C.ink};
          }
          .ipdi2-filter-tab {
            flex: 1;
            background: transparent;
            border: none;
            font-family: 'JetBrains Mono', monospace;
            font-size: 11px;
            letter-spacing: 0.03em;
            padding: 12px 6px;
            cursor: pointer;
            color: ${C.ink};
            border-right: 1px solid ${C.border};
          }
          .ipdi2-filter-tab:last-child { border-right: none; }
          .ipdi2-filter-tab.active { background: ${C.ink}; color: #fff; }

          .ipdi2-filter-group { display: none; }
          .ipdi2-filter-group.active { display: block; }

          /* Hero grid: no dedicated blank side on stacked mobile layout, so hide it */
          .ipdi2-hero-grid { display: none !important; }
        }

        /* Mobile */
        @media (max-width: 640px) {
          .ipdi2-container { padding-left: 18px !important; padding-right: 18px !important; }
          .ipdi2-hero-pad { padding-top: 48px !important; padding-bottom: 40px !important; }
          .ipdi2-section-pad { padding-top: 52px !important; padding-bottom: 52px !important; }
          .ipdi2-badge-row { font-size: 11px !important; }
          .ipdi2-hero-actions { flex-direction: column !important; align-items: stretch !important; }
          .ipdi2-hero-actions button { justify-content: center !important; width: 100%; }
          .ipdi2-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 20px !important; }
          .ipdi2-bento-grid { grid-template-columns: 1fr !important; }
          .ipdi2-why-grid { grid-template-columns: 1fr !important; }
          .ipdi2-why-item { border-right: none !important; border-bottom: 1px solid ${C.border}; }
          .ipdi2-why-item:last-child { border-bottom: none; }
          .ipdi2-job-row { flex-direction: column !important; align-items: flex-start !important; }
          .ipdi2-job-row .ipdi2-btn-outline { width: 100%; justify-content: center; margin-top: 4px; }
          .ipdi2-search-wrap { max-width: 100% !important; }
          .ipdi2-featured-panel { padding: 28px 20px !important; }
          .ipdi2-tag-row { gap: 8px !important; }
        }

        @media (max-width: 400px) {
          .ipdi2-stats-grid { grid-template-columns: 1fr 1fr !important; gap: 16px !important; }
        }
      `}</style>

      {/* HERO */}
      <section>
        <div className="ipdi2-container ipdi2-hero-pad" style={{ maxWidth: 1280, margin: "0 auto", padding: "72px 32px 60px", position: "relative" }}>
          {/* Grid background — confined to the hero text wrapper only, fades in on the blank right side (desktop). Hidden on mobile via CSS. */}
          <div
            className="ipdi2-hero-grid"
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `linear-gradient(${C.border} 1px, transparent 1px), linear-gradient(90deg, ${C.border} 1px, transparent 1px)`,
              backgroundSize: "50px 50px",
              WebkitMaskImage: "linear-gradient(to right, transparent 0%, transparent 55%, black 98%)",
              maskImage: "linear-gradient(to right, transparent 0%, transparent 55%, black 98%)",
              pointerEvents: "none",
            }}
          />

          <div className="ipdi2-badge-row" style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 10, border: `1.5px solid ${C.ink}`, padding: "9px 16px", marginBottom: 34, maxWidth: "100%" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.orange, display: "inline-block", flexShrink: 0 }} />
            <span className="ipdi2-mono" style={{ fontSize: 12, letterSpacing: "0.1em" }}>
              120+ NEW ROLES POSTED THIS MONTH
            </span>
          </div>

          <h1 className="ipdi2-serif" style={{ position: "relative", fontSize: "clamp(34px, 9vw, 76px)", lineHeight: 1.08, fontWeight: 700, maxWidth: 900, margin: 0 }}>
            Find the Work
            <br />
            That <em style={{ color: C.orange, fontWeight: 500 }}>Builds Your Future.</em>
          </h1>

          <p style={{ position: "relative", fontSize: "clamp(15px, 2vw, 18px)", lineHeight: 1.7, color: "#3f3f3f", maxWidth: 540, margin: "26px 0 0" }}>
            India's dedicated job portal for architects, interior designers and AEC
            professionals — verified openings from studios that are actually hiring.
          </p>

          <div className="ipdi2-hero-actions" style={{ position: "relative", display: "flex", gap: 16, marginTop: 36, flexWrap: "wrap" }}>
            {/* <button className="ipdi2-btn-outline" onClick={handleStudentRegister}>Register to Apply</button> */}


             <button className="ipdi2-btn-outline" onClick={handleBrowseOpenings}>
              Browse Openings <ArrowRight size={15} />
            </button>

<a 
  href={portalLink("/student-register")} 
  className="ipdi2-btn-fill"
  style={{ textDecoration: "none", display: "inline-flex" }}
>
  Register to Apply
</a>

            {/* <button className="ipdi2-btn-fill" onClick={handleBrowseOpenings}>
              BROWSE OPENINGS <ArrowRight size={15} />
            </button> */}
          </div>
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, position: "relative" }}>
          <div className="ipdi2-container" style={{ maxWidth: 1280, margin: "0 auto", padding: "36px 32px" }}>
            <div className="ipdi2-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 24 }}>
              {STATS.map((s, i) => (
                <React.Fragment key={s.label}>
                  {i === 2 && <div className="ipdi2-stats-divider" />}
                  <div className={`ipdi2-stat-item ipdi2-stat-${i}`}>
                    <p className="ipdi2-serif" style={{ fontSize: "clamp(30px, 5vw, 44px)", fontWeight: 700, color: C.orange, margin: 0 }}>
                      {s.value}
                    </p>
                    <p className="ipdi2-mono" style={{ fontSize: 12, letterSpacing: "0.08em", color: "#555", marginTop: 6 }}>
                      {s.label.toUpperCase()}
                    </p>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED JOB */}
      <section style={{ background: C.cream }}>
        <div className="ipdi2-container ipdi2-section-pad" style={{ maxWidth: 1280, margin: "0 auto", padding: "72px 32px 40px" }}>
          <Eyebrow>FEATURED OPENING</Eyebrow>
          <h2 className="ipdi2-serif" style={{ fontSize: "clamp(26px, 5vw, 44px)", fontWeight: 700,  margin: "0 0 40px" }}>
            A Role Worth <em style={{ color: C.orange, fontWeight: 500   }}>Applying For</em>
          </h2>
          {/* <div style={{ height: 1, background: C.border, margin: "28px 0 40px" }} /> */}

          <div className="ipdi2-card ipdi2-featured-grid" style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1fr) minmax(320px, 1.1fr)", minHeight: 420 }}>
            <div
              className="ipdi2-featured-image"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=900&q=80')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative",
              }}
            >
              <span className="ipdi2-mono" style={{ position: "absolute", top: 20, left: 20, background: C.orange, color: "#fff", fontSize: 11.5, letterSpacing: "0.08em", padding: "7px 14px" }}>
                FEATURED
              </span>
            </div>
            <div className="ipdi2-featured-panel" style={{ background: C.dark, color: "#fff", padding: "44px 40px", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <span style={{ width: 18, height: 1.5, background: C.orange }} />
                <span className="ipdi2-mono" style={{ fontSize: 12, letterSpacing: "0.1em", color: C.orange }}>
                  ARCHITECTURE
                </span>
              </div>
              <h3 className="ipdi2-serif" style={{ fontSize: "clamp(24px, 4vw, 30px)", fontWeight: 700, margin: "0 0 14px" }}>
                Senior Architect
              </h3>
              <p style={{ fontSize: 15.5, lineHeight: 1.7, color: "#c9c9c9", maxWidth: 440 }}>
                KRA Design Studio is hiring a senior architect to lead project documentation
                and client delivery across residential and hospitality projects in Pune.
              </p>

              <div className="ipdi2-tag-row" style={{ display: "flex", flexWrap: "wrap", gap: 10, margin: "22px 0" }}>
                {["AUTOCAD", "REVIT / BIM", "SITE MANAGEMENT", "DESIGN STUDIO", "CONSTRUCTION DOCS"].map((tag) => (
                  <span key={tag} className="ipdi2-mono" style={{ border: "1px solid rgba(255,255,255,0.25)", color: "#d8d8d8", fontSize: 11.5, letterSpacing: "0.04em", padding: "8px 12px" }}>
                    {tag}
                  </span>
                ))}
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 22, fontSize: 13, color: "#a8a8a8", borderTop: "1px solid rgba(255,255,255,0.14)", paddingTop: 20, marginTop: "auto", marginBottom: 24 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <Clock size={14} /> 6+ YRS EXPERIENCE
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <MapPin size={14} /> PUNE, MH
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <Briefcase size={14} /> FULL-TIME
                </span>
              </div>

              {/* <button className="ipdi2-btn-fill" style={{ alignSelf: "flex-start" }} onClick={handleStudentLogin}>
                VIEW & APPLY <ArrowRight size={15} />
              </button> */}


<a 
  href={portalLink("/student-login")} 
 className="ipdi2-btn-fill" style={{ alignSelf: "flex-start" }}
>
  VIEW & APPLY <ArrowRight size={15} />
</a>

            </div>
          </div>
        </div>
      </section>

     

      {/* WHY APPLY - numbered */}
      <section style={{ background: C.cream }}>
        <div className="ipdi2-container ipdi2-section-pad" style={{ maxWidth: 1280, margin: "0 auto", padding: "80px 32px" }}>
          <Eyebrow>WHY APPLY THROUGH IPDI</Eyebrow>
          <h2 className="ipdi2-serif" style={{ fontSize: "clamp(26px, 5vw, 44px)", fontWeight: 700, margin: "0 0 12px" }}>
            Applying, <em style={{ color: C.orange, fontWeight: 500 }}>Made Confident.</em>
          </h2>
          <p style={{ color: "#555", fontSize: 16, maxWidth: 560, margin: "0 0 44px" }}>
            Not just another listings board. A portal built specifically for how design
            professionals get hired.
          </p>

          <div className="ipdi2-why-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", border: `1px solid ${C.border}` }}>
            {WHY_ITEMS.map((item, i) => (
              <div
                key={item.n}
                className="ipdi2-why-item"
                style={{
                  padding: "34px 26px",
                  borderRight: i !== WHY_ITEMS.length - 1 ? `1px solid ${C.border}` : "none",
                  background: item.highlight ? C.ink : "transparent",
                  color: item.highlight ? "#fff" : C.ink,
                }}
              >
                <span className="ipdi2-mono ipdi2-why-num" style={{ fontSize: 22, color: C.orange, display: "block", marginBottom: 20 }}>
                  {item.n}
                </span>
                <p className="ipdi2-why-title" style={{ fontWeight: 700, fontSize: 18, margin: "0 0 10px" }}>{item.title}</p>
                <p className="ipdi2-why-desc" style={{ fontSize: 14, lineHeight: 1.6, color: item.highlight ? "#c9c9c9" : "#666", margin: 0 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>


       {/* CATEGORY BENTO - DARK */}
      <section style={{ background: C.dark, color: "#fff" }}>
        <div className="ipdi2-container ipdi2-section-pad" style={{ maxWidth: 1280, margin: "0 auto", padding: "80px 32px" }}>
          <Eyebrow>BROWSE BY DISCIPLINE</Eyebrow>
          <h2 className="ipdi2-serif" style={{ fontSize: "clamp(26px, 5vw, 44px)", fontWeight: 700, margin: "0 0 44px" }}>
            Every Opening, <em style={{ color: C.orange, fontWeight: 500 }}>One Portal.</em>
          </h2>

          <div className="ipdi2-bento-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 2 }}>
            {CATEGORIES.map((cat) => (
              <div key={cat.tag} className="ipdi2-cat-card" style={{ background: cat.bg, minHeight: 240 }}>
                <div className="ipdi2-cat-card-inner" style={{ padding: "34px 28px" }}>
                  <span className="ipdi2-mono ipdi2-cat-tag" style={{ fontSize: 11, letterSpacing: "0.08em", color: C.orange, border: `1px solid ${C.orange}`, padding: "5px 10px", alignSelf: "flex-start", marginBottom: 18 }}>
                    {cat.tag}
                  </span>
                  <p style={{ fontWeight: 700, fontSize: 19, lineHeight: 1.35, margin: "0 0 10px" }}>{cat.title}</p>
                  <p style={{ fontSize: 13.5, color: "#a8a8a8", margin: "0 0 20px", lineHeight: 1.5 }}>{cat.desc}</p>
                  <button className="ipdi2-btn-outline-light" onClick={handleBrowseOpenings}>
                    {cat.cta} <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LISTINGS — sidebar filters + sortable results */}
      <section id="ipdi-listings" style={{ background: C.cream, borderTop: `1px solid ${C.border}` }}>
        <div className="ipdi2-container ipdi2-section-pad" style={{ maxWidth: 1280, margin: "0 auto", padding: "80px 32px 40px" }}>
          <Eyebrow>OPEN ROLES</Eyebrow>
          <h2 className="ipdi2-serif" style={{ fontSize: "clamp(26px, 5vw, 44px)", fontWeight: 700, margin: "0 0 44px" }}>
            Search Current <em style={{ color: C.orange, fontWeight: 500 }}>Openings</em>
          </h2>

          <div className="ipdi2-layout" style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 48, alignItems: "start" }}>
            {/* SIDEBAR */}
            <aside className="ipdi2-sidebar" style={{ position: "sticky", top: 24 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 15 }}>
                  <SlidersHorizontal size={16} /> Filters
                </span>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearAll}
                    className="ipdi2-mono"
                    style={{ background: "none", border: "none", color: C.orange, fontSize: 11.5, cursor: "pointer", letterSpacing: "0.04em" }}
                  >
                    CLEAR ({activeFilterCount})
                  </button>
                )}
              </div>

              <div className="ipdi2-filter-tabs">
                <button
                  className={`ipdi2-filter-tab ${activeFilterTab === "discipline" ? "active" : ""}`}
                  onClick={() => setActiveFilterTab("discipline")}
                >
                  DISCIPLINE
                </button>
                <button
                  className={`ipdi2-filter-tab ${activeFilterTab === "type" ? "active" : ""}`}
                  onClick={() => setActiveFilterTab("type")}
                >
                  JOB TYPE
                </button>
                <button
                  className={`ipdi2-filter-tab ${activeFilterTab === "experience" ? "active" : ""}`}
                  onClick={() => setActiveFilterTab("experience")}
                >
                  EXPERIENCE
                </button>
              </div>

              <div className={`ipdi2-filter-group ${activeFilterTab === "discipline" ? "active" : ""}`} style={{ marginBottom: 30 }}>
                <p className="ipdi2-mono" style={{ fontSize: 11.5, letterSpacing: "0.08em", color: "#666", marginBottom: 14 }}>
                  DISCIPLINE
                </p>
                {CAT_FILTERS.map((c) => (
                  <label key={c} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, fontSize: 14, cursor: "pointer" }}>
                    <input type="checkbox" className="ipdi2-checkbox" checked={categories.includes(c)} onChange={() => toggle(categories, setCategories, c)} />
                    {c}
                  </label>
                ))}
              </div>

              <div className={`ipdi2-filter-group ${activeFilterTab === "type" ? "active" : ""}`} style={{ marginBottom: 30 }}>
                <p className="ipdi2-mono" style={{ fontSize: 11.5, letterSpacing: "0.08em", color: "#666", marginBottom: 14 }}>
                  JOB TYPE
                </p>
                {TYPE_FILTERS.map((t) => (
                  <label key={t} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, fontSize: 14, cursor: "pointer" }}>
                    <input type="checkbox" className="ipdi2-checkbox" checked={types.includes(t)} onChange={() => toggle(types, setTypes, t)} />
                    {t}
                  </label>
                ))}
              </div>

              <div className={`ipdi2-filter-group ${activeFilterTab === "experience" ? "active" : ""}`}>
                <p className="ipdi2-mono" style={{ fontSize: 11.5, letterSpacing: "0.08em", color: "#666", marginBottom: 14 }}>
                  EXPERIENCE
                </p>
                {EXP_FILTERS.map((e) => (
                  <label key={e} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, fontSize: 14, cursor: "pointer" }}>
                    <input type="checkbox" className="ipdi2-checkbox" checked={experiences.includes(e)} onChange={() => toggle(experiences, setExperiences, e)} />
                    {e}
                  </label>
                ))}
              </div>
            </aside>

            {/* RESULTS */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
                <p className="ipdi2-mono" style={{ fontSize: 13, color: "#666", margin: 0 }}>
                  {filtered.length} {filtered.length === 1 ? "ROLE" : "ROLES"} FOUND
                </p>
                <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 8, border: `1.5px solid ${C.ink}`, padding: "9px 14px" }}>
                  <span className="ipdi2-mono" style={{ fontSize: 12, color: "#666" }}>SORT:</span>
                  <select
                    className="ipdi2-select"
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    style={{ border: "none", background: "transparent", fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5, color: C.ink, cursor: "pointer", outline: "none" }}
                  >
                    {SORTS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ipdi2-search-wrap" style={{ display: "flex", alignItems: "center", gap: 10, border: `1.5px solid ${C.ink}`, padding: "13px 18px", maxWidth: 520, marginBottom: 24 }}>
                <Search size={17} color={C.ink} style={{ flexShrink: 0 }} />
                <input
                  type="text"
                  className="ipdi2-input"
                  placeholder="Search by role, studio, or city"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ border: "none", outline: "none", fontFamily: "'Inter', sans-serif", fontSize: 14.5, flex: 1, background: "transparent", minWidth: 0 }}
                />
                {query && (
                  <button aria-label="Clear search" onClick={() => setQuery("")} style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", flexShrink: 0 }}>
                    <X size={16} color="#888" />
                  </button>
                )}
              </div>

              {filtered.length === 0 ? (
                <div style={{ padding: "48px 0", textAlign: "center", border: `1px dashed ${C.border}` }}>
                  <p style={{ fontSize: 17, fontWeight: 700 }}>No roles match those filters yet.</p>
                  <p style={{ fontSize: 14, marginTop: 6, color: "#777" }}>Try clearing a filter or searching a different term.</p>
                  <button onClick={clearAll} className="ipdi2-btn-outline" style={{ marginTop: 20 }}>
                    CLEAR ALL FILTERS
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ display: "grid", gap: 14 }}>
                    {visibleJobs.map((job) => {
                      const isSaved = saved.includes(job.id);
                      return (
                        <div
                          key={job.id}
                          className="ipdi2-card ipdi2-job-row"
                          style={{ border: `1px solid ${C.border}`, padding: "26px 28px", display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 18, background: "#fff" }}
                        >
                          <div>
                            <p className="ipdi2-serif" style={{ fontWeight: 700, fontSize: 21, margin: "0 0 4px" }}>
                              {job.title}
                            </p>
                            <p style={{ fontSize: 14.5, color: "#555", margin: "0 0 12px" }}>{job.company}</p>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 18, fontSize: 12.5 }}>
                              <span className="ipdi2-mono" style={{ display: "flex", alignItems: "center", gap: 6, color: "#666" }}>
                                <MapPin size={13} /> {job.location.toUpperCase()}
                              </span>
                              <span className="ipdi2-mono" style={{ display: "flex", alignItems: "center", gap: 6, color: C.orange }}>
                                <Briefcase size={13} /> {job.type.toUpperCase()}
                              </span>
                              <span className="ipdi2-mono" style={{ display: "flex", alignItems: "center", gap: 6, color: "#666" }}>
                                {job.experience.toUpperCase()}
                              </span>
                              <span className="ipdi2-mono" style={{ display: "flex", alignItems: "center", gap: 6, color: "#999" }}>
                                <Clock size={13} /> {job.posted.toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <button
                              className="ipdi2-save"
                              onClick={() => toggleSave(job.id)}
                              aria-label={isSaved ? "Remove from saved jobs" : "Save job"}
                            >
                              {isSaved ? <BookmarkCheck size={17} color={C.orange} /> : <Bookmark size={17} color={C.ink} />}
                            </button>
                            {/* <button className="ipdi2-btn-outline" onClick={handleStudentLogin}>
                              APPLY NOW <ArrowRight size={14} />
                            </button> */}


<a 
  href={portalLink("/student-login")} 
  className="ipdi2-btn-outline"
  style={{ textDecoration: "none" }}
>
   APPLY NOW <ArrowRight size={14} />
</a>

                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {visible < filtered.length && (
                    <div style={{ textAlign: "center", marginTop: 32 }}>
                      <button className="ipdi2-btn-outline" onClick={() => setVisible((v) => v + 6)}>
                        LOAD MORE ROLES
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      {/* <section style={{ background: C.cream, borderTop: `1px solid ${C.border}` }}>
        <div className="ipdi2-container ipdi2-section-pad ipdi2-how-grid" style={{ maxWidth: 1280, margin: "0 auto", padding: "80px 32px 100px", display: "grid", gridTemplateColumns: "minmax(240px, 1fr) minmax(320px, 1.4fr)", gap: 48 }}>
          <div>
            <Eyebrow>FROM APPLY TO OFFER</Eyebrow>
            <p className="ipdi2-serif" style={{ fontSize: "clamp(40px, 8vw, 72px)", fontWeight: 700, color: C.orange, margin: 0, fontStyle: "italic" }}>
              100%
            </p>
            <p className="ipdi2-mono" style={{ fontSize: 12, letterSpacing: "0.08em", color: "#555", marginTop: 8 }}>
              PLACEMENT ASSISTANCE
            </p>
            <p style={{ fontSize: 15, lineHeight: 1.7, color: "#555", marginTop: 18, maxWidth: 320 }}>
              Every candidate who applies through IPDI gets support until they land
              the right role.
            </p>
          </div>

          <div>
            {PROCESS.map((step, i) => (
              <div
                key={step.n}
                style={{ display: "flex", gap: 24, padding: "22px 0", borderTop: i === 0 ? `1px solid ${C.border}` : "none", borderBottom: `1px solid ${C.border}` }}
              >
                <span className="ipdi2-serif" style={{ fontStyle: "italic", fontSize: 22, color: C.orange, minWidth: 40 }}>
                  {step.n}
                </span>
                <div>
                  <p style={{ fontWeight: 700, fontSize: 16.5, margin: "0 0 4px" }}>{step.title}</p>
                  <p style={{ fontSize: 14, color: "#666", margin: 0, lineHeight: 1.5 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}
    </div>
  );
}